import React, { useCallback, useEffect, useState } from 'react';
import T from 'components/common/text/T';
import { useTranslation } from 'react-i18next';
import { useDidUpdateEffect } from 'lib/utils';
import { useShallowAppSelector } from 'store/hooks';
import storage, { setCookie, keys } from 'lib/storage';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { AppActions, AuthActions, UserActions, UtilActions } from 'store/actionCreators';
import CodeRegistration from 'components/auth/CodeRegistration';
import { signInUser, loginAdminHistory } from 'store/modules/auth';
import { companyType, pageUrl } from 'lib/mapper';
import { isLogInSelector } from 'store/modules/auth';
import useInput from 'lib/hooks/useInput';
import { IUserProfile } from 'lib/types/userTypes';
import { signOut } from 'store/modules/auth';
import packageJson from '../../../package.json';
import _ from 'lodash';
import { isClinicUserSelector } from 'store/modules/user';
import { oemState } from 'lib/store';

export default function CodeRegistrationContainer() {
  const {
    language,
    signInUrl,
    signInUrlSuccess,
    signUpUrl,
    signUpUrlSuccess,
    editLegalAgreementSuccess,
    agreement,
    agreementSuccess,
    isLogIn,
    fetchProfile: { profile: userProfile },
    fetchProfileSuccess,
    isClinicUser,
    updateUserTypeSuccess,
  }: {
    language: string;
    signInUrl: string;
    signInUrlSuccess: boolean;
    signUpUrl: string;
    signUpUrlSuccess: boolean;
    editLegalAgreementSuccess: boolean;
    agreement: any;
    agreementSuccess: boolean;
    isLogIn: any;
    fetchProfile: { profile: IUserProfile };
    fetchProfileSuccess: boolean;
    isClinicUser: boolean;
    updateUserTypeSuccess: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    signInUrl: state.auth.signInAuth.data?.connectUrl,
    signInUrlSuccess: state.auth.signInAuth.success,
    signUpUrl: state.auth.signUpAuth.data?.connectUrl,
    signUpUrlSuccess: state.auth.signUpAuth.success,
    userProfileSuccess: state.user.fetchProfile.success,
    editLegalAgreementSuccess: state.auth.editLegalAgreement.success,
    agreement: state.util.agreementList.data,
    agreementSuccess: state.util.agreementList.success,
    isLogIn: isLogInSelector(state),
    fetchProfile: state.user.fetchProfile.data || {},
    fetchProfileSuccess: state.user.fetchProfile.success,
    isClinicUser: isClinicUserSelector(state),
    updateUserTypeSuccess: state.user.updateUserType.success,
  }));
  const { ipcRenderer } = window;
  const { t } = useTranslation();
  const history = useHistory();
  const location = history.location;
  const { pathname, search, state } = location as { pathname: string; search: string; state: any };
  const queryParse = queryString.parse(search);
  const from = state?.hasOwnProperty('from') ? state.from : { pathname };
  // NOTE: ErrorContainer 에서 returnPath 넘겨줬을 경우, PrivateRoute에서 from을 넘겨줬을경우
  const returnPath = queryParse?.returnPath || from.pathname;
  const accessToken = queryParse?.accessToken as string | null;
  const isAutoLogin = queryParse?.autoLogin;
  const isFailure = !!queryParse?.failure;

  const isAgreementModal = useInput(false);
  const signInEmail = useInput('');
  const [isUserType, setIsUserType] = useState<boolean>(false);
  const [willSelectLeagal, setWillSelectLeagal] = useState<boolean>(true);
  // SECTION: state
  const handleCheckUpdateVersion = () => {
    if (!ipcRenderer) return;
    const checkedVersion = packageJson.version;
    ipcRenderer.send('updateCheck', {
      versions: checkedVersion,
    });
  };

  const loginFailurePopup = useCallback(() => {
    history.replace(location.pathname);

    AppActions.add_popup({
      isOpen: true,
      title: <T>GLOBAL_ALERT</T>,
      content: t('ALARM_LOGIN_FAILURE'),
      isTitleDefault: true,
      isContentDefault: true,
      onClick() {
        return;
      },
    });
  }, []);

  useEffect(() => {
    // TEST:
    if (Object.keys(queryParse).length > 0) {
      //queryParse 존재 여부 확인
      if (!!isFailure) {
        loginFailurePopup();
      } else if (!!accessToken) {
        // TODO: accessToken으로 유저 확인 API
        // 토큰 확인 후 isExtraInfo 추가 필요 정보 확인
        AppActions.set_dim(true);
        setCookie(keys.auto_login, isAutoLogin);
        AuthActions.set_access_token(accessToken);
        UserActions.fetch_profile_request();
      } else {
        loginFailurePopup();
      }
    }
  }, []);

  useEffect(() => {
    handleCheckUpdateVersion();
    UtilActions.fetch_legal_agreement_list_request({ language: language.toUpperCase() });
  }, []);

  useDidUpdateEffect(() => {
    if (!!userProfile && !!isLogIn) {
      const userMandatoryList = userProfile?.agreementList?.map((item: any) => {
        //isAgreement 체크하여 배열로 리턴
        return !!item.isAgreement;
      }, []);
      const isWillSelectLeagal = userMandatoryList.some((item: any) => {
        return item === false;
      });

      let isUserType = false;
      _.map(companyType, (item, index) => {
        if (item.id === userProfile.userTypeIdx) {
          isUserType = true;
        }
      });
      if (!isWillSelectLeagal && isUserType) {
        storage.remove('returnPath');
        UserActions.set_login_success();
        if (!!isClinicUser) {
          history.push(pageUrl.quickstart.index);
        } else {
          history.push(pageUrl.dashboard.index);
        }
      } else {
        setWillSelectLeagal(isWillSelectLeagal);
        setIsUserType(isUserType);
        isAgreementModal.setValue(true);
      }
    }
  }, [userProfile, isLogIn, isClinicUser]);

  useDidUpdateEffect(() => {
    if (userProfile) {
      loginAdminHistory(userProfile?.email);
    }
  }, [userProfile]);

  // SECTION: DidUpdate

  const handleNavigateDof = (url: string) => {
    storage.set('syncDim', 'true');
    window.location.assign(url);
    storage.set('returnPath', returnPath);
  };

  // returhPath 저장 후 이동
  useDidUpdateEffect(() => {
    if (signInUrlSuccess) {
      if (!!signInEmail.value) {
        handleNavigateDof(
          `${signInUrl}&email=${signInEmail.value}&language=${language}&oem=${oemState()}`,
        );
      } else {
        handleNavigateDof(`${signInUrl}&language=${language}&oem=${oemState()}`);
      }
    }
  }, [signInUrlSuccess === true, signInEmail.value]);

  useDidUpdateEffect(() => {
    if (signUpUrlSuccess) {
      handleNavigateDof(`${signUpUrl}&language=${language}`);
    }
  }, [signUpUrlSuccess === true]);

  useDidUpdateEffect(() => {
    if (editLegalAgreementSuccess && isLogIn) {
      UserActions.fetch_profile_request();
      storage.remove('returnPath');
      UserActions.set_login_success();
      history.push(pageUrl.dashboard.index);
    }
  }, [editLegalAgreementSuccess === true]);

  useDidUpdateEffect(() => {
    if (updateUserTypeSuccess && isLogIn) {
      UserActions.fetch_profile_request();
      storage.remove('returnPath');
      UserActions.set_login_success();
      if (!!isClinicUser) {
        history.push(pageUrl.quickstart.index);
      } else {
        history.push(pageUrl.dashboard.index);
      }
    }
  }, [updateUserTypeSuccess === true]);

  useEffect(() => {
    if (ipcRenderer) {
      handleAutoLogin();
    }
  }, []);

  const handleAutoLogin = async () => {
    const isAutoLogin = await ipcRenderer.invoke('autoLoginAsync', {});

    console.log('isAutoLogin', isAutoLogin);

    if (!!isAutoLogin.autoLogin && !!isAutoLogin.accessToken) {
      signInUser(isAutoLogin.accessToken);
    }
    if (!!isAutoLogin.offlineToOnline && !!isAutoLogin.accessToken) {
      signInUser(isAutoLogin.accessToken);
    }
  };

  const handleSubmitLegal = (legalArr: any[], userType: Number) => {
    let submitData = {
      accessToken,
      policy: legalArr,
      userTypeIdx: userType,
    };
    // console.log('hadleSubmitLegal _______ ', submitData);
    AuthActions.edit_legal_agreement_request(submitData);
  };

  const handleUpdateUserType = (userType: Number) => {
    let submitData = {
      userTypeIdx: userType,
    };
    UserActions.update_user_type_request(submitData);
  };
  const hadleCancelLegal = () => {
    history.push(pageUrl.home);
    isAgreementModal.setValue(false);
    AppActions.set_dim(false);
    signOut();
  };

  return (
    <CodeRegistration
      willSelectLeagal={willSelectLeagal}
      isUserType={isUserType}
      signInEmail={signInEmail}
      isAgreementModal={isAgreementModal.value}
      onSubmitLegal={handleSubmitLegal}
      onSubmitUpdateUserType={handleUpdateUserType}
      onCancelLegal={hadleCancelLegal}
      onCheckUpdateVersion={handleCheckUpdateVersion}
    />
  );
}
